import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import "./MyAccountOrderCard.css";
import CarePlusOrderCard from "../CarePlus/CarePlusOrderCard/CarePlusOrderCard";
import { Link } from "react-router-dom";
import * as translator from "../../../../utils/translator";
import SubmitButton from "../../../../components/SubmitButton/SubmitButton";
import { MainTracker } from "../../../../PixelTrackers/MainTracker";
import MyAccountContentHeader from "../MyAccountContentHeader/MyAccountContentHeader";
import CopyToClipboard from "../../../../components/copyToClipboard/copyToClipboard";
import { toLocaleDateString } from "../../../../utils/helpers";
import MyAccountOrderDetailsDisclaimer from "../MyAccountOrderDisclaimer/MyAccountOrderDisclaimer";
import "../MyAccountOrderDisclaimer/MyAccountOrderDisclaimer.css";
import { calculateColorAndDescription } from "../MyAccountOrderDisclaimer/MyAccountOrderDisclaimerPreProcess";
import { DELIVERED, SENT_TO_CUSTOMER, WAIT_CUSTOMER_ADDITIONAL_ACTIONS, ID_VERIFICATION_IN_PROGRESS, ADDRESS_VERIFY } from "../../../../utils/consts";
import OrderActionBanner from "./OrderActionBanner";
import ReorderActions from "../ReorderActions/ReorderActions";
import { WlReorderVariant } from "../../../../utils/wlReorderSubscriptionExperiment";
import CalendarIcon from '../../../../icons/calendar.svg';
import * as http from "../../../../utils/http";
import { formatTimestamp, getLocaleFormatOptions } from "../../../../utils/timestampFormatter";

const FIFTEEN_MINUTES = 15 * 60 * 1000;

interface MyAccountOrderCardProps {
  defaultImage?: string;
  order: MyAccountOrder;
  /** WL reorder test arm, assigned by My Orders. */
  wlReorderVariant?: WlReorderVariant | null;
}

const MyAccountOrderCard: React.FC<MyAccountOrderCardProps> = ({
  defaultImage,
  order,
  wlReorderVariant,
}) => {
  const navigate = useNavigate();
  const { country, language } = useOutletContext<any>();
  const [isMobile, setIsMobile] = useState(false);
  const statusData = useMemo(
    () => {
      const statusData = calculateColorAndDescription(order, language, "my-orders");
      if (order.status === DELIVERED && !order.haveFeedback) {
        statusData.description = statusData.description.replace(
          "#order-details-feedback",
          `/de/account/my-orders/${order.hashID}/details#order-details-feedback`
        );
      }
      return statusData;
    },
    [order.status]
  );

  const showConsultationBooking = useMemo(() => {
    if (!order.orderTimekitBooking || order.status === 'cancelled') return false;
    const startTime = new Date(order.orderTimekitBooking.start).getTime();
    const now = new Date().getTime();
    return now < startTime + FIFTEEN_MINUTES && now < startTime + FIFTEEN_MINUTES;
  }, [order.orderTimekitBooking]);

  const getRecipeCopy = (event: React.MouseEvent<HTMLAnchorElement>) => {

    event.stopPropagation();

    const attachment = order.recipeCopy;
    http
      .postToServer(
        `download-recipe-copy`,
        {
          attachmentID: attachment?.id
        },
        [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.GENERATE_FILE]
      )
      .then((response: any) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: attachment?.mime_type })
        );
        const link = document.createElement("a");
        link.className = "blob-link";
        link.href = url;
        link.setAttribute("download", attachment?.display_name ?? "");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => {
        // Handle error
      });
  };

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 1200);
    };

    // Set initial value
    checkIfMobile();

    // Add event listener for resize
    window.addEventListener("resize", checkIfMobile);

    // Cleanup event listener
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  if (!language) {
    return <></>;
  }

  if (order.carePlusContext) {
    // A first Care+ order can still wait for post-checkout ID / body photos (GB/CH): same banner as other orders.
    const isWaitingVerification =
      order.status === WAIT_CUSTOMER_ADDITIONAL_ACTIONS || order.status === ID_VERIFICATION_IN_PROGRESS;
    return (
      <>
        <CarePlusOrderCard order={order} defaultImage={defaultImage} />
        {isWaitingVerification && (
          <OrderActionBanner
            hashID={order.hashID}
            country={country}
            language={language}
            needsPhotoUpload={order.pendingFlags?.needsPhotoUpload}
            needsIdVerification={order.pendingFlags?.needsIdVerification}
            idVerificationInProgress={order.pendingFlags?.idVerificationInProgress}
            needsDoseProof={order.pendingFlags?.needsDoseProof}
          />
        )}
      </>
    );
  }

  let treatmentFee: any;


  treatmentFee = Number(
    order.prices["treatmentFee"]?.split(" ")[0] || 0
  );


  const treatmentFeeDiscount: number = Number(
    order.prices["treatmentFeeDiscount"]?.split(" ")[0] || 0
  );
  const currencySymbol: string = String(
    order.prices["treatmentFee"]?.split(" ")[1]
      || order.prices["planFee"]?.split(" ")[1]
      || order.prices["total"]?.split(" ")[1]
      || ""
  );

  const orderPriceTotal = order.prices["total"];
  const orderTreatmentFee = order.prices["treatmentFeeDiscount"]
    ? (treatmentFee + treatmentFeeDiscount).toFixed(2) + " " + currencySymbol
    : order.prices["treatmentFee"];
  // API one-time WL fold: drug / planFee / discount / total (same keys as details).
  const foldedPriceRows = order.prices.planFee
    ? ([
        ["MyOrders/details/drug", order.prices.drug],
        ["MyOrders/details/planFee", order.prices.planFee],
        ["MyOrders/details/discount", order.prices.discount],
      ] as const).filter(([, value]) => value != null)
    : null;

  const dateOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  const products =
    order.orderType == "prescription_only" ? [] : order["cartViewProducts"];
  const productsView = products.map((product) => {
    return {
      image: product.imageSrc ? product.imageSrc : defaultImage,
      title: product.titleView,
    };
  });

  const deliveryEvent =
    order.shipmentEvents && order.shipmentEvents.length > 0
      ? order.shipmentEvents[0]
      : null;

  const pricesElement = (
    <span className="account-order-prices">
      {foldedPriceRows
        ? foldedPriceRows.map(([labelKey, value]) => (
            <p key={labelKey} className="order-price-breakdown-row">
              {translator.translate(language, "my_account", labelKey)}: {value}
            </p>
          ))
        : null}
      <p className="order-price-without-treatmentfee">
        {translator.translate(language, "my_account", "MyOrders/paid") + ":"}{" "}
        <span className="order-price-without-treatmentfee">
          {orderPriceTotal}
        </span>
      </p>
      {!foldedPriceRows && orderTreatmentFee ? (
        <p className="order-treatmentfee">
          {"+ " +
            translator.translate(
              language,
              "my_account",
              "MyOrders/cardTreatmentFee"
            ) +
            " " +
            orderTreatmentFee}
        </p>
      ) : null}
      {!foldedPriceRows && order.prices["treatmentFeeDiscount"] ? (
        <p className="order-treatmentfee-discount">
          {"*" +
            order.prices["treatmentFeeDiscount"] +
            " " +
            translator.translate(
              language,
              "my_account",
              "MyOrders/discountApplied"
            )}
        </p>
      ) : null}
    </span>
  );

  return (
    <div
      className="account-order-card"
      onClick={
        !isMobile
          ? () => {
              MainTracker.track("click", "Go to Order Details");
              navigate({
                pathname: `/${country}/account/my-orders/${order.hashID}/details`,
              });
            }
          : undefined
      }
    >
      <div className="account-order-card-top">
        <div className="account-order-card-header">
          <MyAccountContentHeader
            title={order.hashID ? order.hashID : ""}
            titleFurnish={
              <CopyToClipboard textToCopy={order.hashID ? order.hashID : ""} />
            }
            description={
              // A specific address_verify sub-status has its own text, otherwise the generic status text is shown.
              (order.status === ADDRESS_VERIFY && order.sub_status && translator.translate(language, "my_account", `MyOrders/status/address_verify_sub_statuses/${order.sub_status}`)) ||
              translator.translate(
                language,
                "my_account",
                order.status === "sent_to_pharmacy" && order.orderType !== "prescription_only"
                  ? "MyOrders/details/timelineStatus/sent_to_pharmacy"
                  : `MyOrders/status/${order.status}`
              )
            }
            descriptionClass={`order-status order-status-description ${statusData.class}`}
            titleClass="order-top-title"
          />
          <span className="accout-order-card-date">
            <p>
              {toLocaleDateString(
                order.createdAt ? order.createdAt : "",
                language,
                dateOptions
              )}
            </p>
          </span>
        </div>
        {statusData.description ? (
          <MyAccountOrderDetailsDisclaimer
            statusColor={order.rejectReason ? "danger" : statusData.class}
            description={statusData.description}
            title={statusData.title || ""}
            showIcon={statusData.showIcon || false}
            cardClass="order-disclaimer-card"
          />
        ) : null}
        {order.status === 'address_verify' &&
          ['reshipment requested by customer', 'reshipment requested by customer timeout'].includes(order.status_reason ?? '') && (
          <div className="card warning order-disclaimer-card" id="my-account-order-card-redelivery-banner">
            <div className="contentWrapper">
              <div className="mainContent">
                <div className="notificationBody">
                  {translator.translate(language, 'my_account', 'MyOrders/details/disclaimerDescriptions/redelivery_address_change_prompt_before_link')}
                  <a
                    className="interactable"
                    style={{ cursor: 'pointer' }}
                    href={`/${country}/account/my-orders/${order.hashID}/details?re=reship_change_address&closeable=1`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      MainTracker.track('click', 'Redelivery address change banner link');
                      navigate({ pathname: `/${country}/account/my-orders/${order.hashID}/details`, search: '?re=reship_change_address&closeable=1' });
                    }}
                  >
                    {translator.translate(language, 'my_account', 'MyOrders/details/disclaimerDescriptions/redelivery_address_change_prompt_link')}
                  </a>
                  {translator.translate(language, 'my_account', 'MyOrders/details/disclaimerDescriptions/redelivery_address_change_prompt_after_link')}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="account-order-body">
          <div className="account-order-images">
            {productsView.length ? (
              productsView.map((productView, index) => (
                <img
                  className="account-order-image"
                  src={productView.image ?? defaultImage}
                  alt={productView.title}
                  key={`${productView.title}-${index}`}
                />
              ))
            ) : (
              <img
                className="account-order-image"
                src={defaultImage}
                alt="only-prescription"
              />
            )}
          </div>
          {!isMobile ? pricesElement : null}
        </div>
      </div>
      {(order.status === WAIT_CUSTOMER_ADDITIONAL_ACTIONS || order.status === ID_VERIFICATION_IN_PROGRESS) && (
        <OrderActionBanner
          hashID={order.hashID}
          country={country}
          language={language}
          needsPhotoUpload={order.pendingFlags?.needsPhotoUpload}
          needsIdVerification={order.pendingFlags?.needsIdVerification}
          idVerificationInProgress={order.pendingFlags?.idVerificationInProgress}
          needsDoseProof={order.pendingFlags?.needsDoseProof}
        />
      )}
      <div className="account-order-card-footer">
        <div className="account-order-card-links">
          <Link
            to={`${order.hashID}/details`}
            className="account-order-card-link"
            onClick={(e) => {
              if (e.stopPropagation) e.stopPropagation();
              MainTracker.track("click", "Go to Order Details");
            }}
          >
            {translator.translate(
              language,
              "my_account",
              "MyOrders/cardDetails"
            )}
          </Link>

          {order.recipeCopy ? (
            <a
              onClick={getRecipeCopy}
              className="account-order-card-link"
            >
              {translator.translate(
                language, 
                "my_account",
                "MyOrders/recipe-copy"
              )}
            </a>
          ) : null}
        </div>
        {isMobile ? pricesElement : null}
        <div
          className={`account-order-card-buttons-and-delivery ${
            isMobile || (order?.orderShipping?.tracking_id && order.sentAt)
              ? "fullWidth"
              : "fitWidth"
          }`}
        >
          {order?.orderShipping?.tracking_id && order.sentAt ? (
            <div className="account-order-card-delivery-details-container">
              <div className="account-order-card-delivery-details">
                {translator.translate(
                  language,
                  "my_account",
                  "MyOrders/trackingNumber"
                ) + ":"}{" "}
                <span className="account-order-card-delivery-details">
                  {order.orderShipping.tracking_id}
                </span>
              </div>
              <div className="account-order-card-delivery-details">
                {translator.translate(
                  language,
                  "my_account",
                  "MyOrders/sentAt"
                ) + ":"}{" "}
                <span className="account-order-card-delivery-details">
                  {toLocaleDateString(order.sentAt, language, dateOptions)}
                </span>
              </div>
            </div>
          ) : null}
          <div className="account-order-card-buttons">
            {order.isAvailableForReorder ? (
              <ReorderActions
                orderHashID={order.hashID}
                country={country}
                language={language}
                reorderClickEvent="Reorder"
                wlSubscriptionOffer={order.wlSubscriptionOffer}
                reorderTrackingContext={order.reorderTrackingContext}
                wlReorderVariant={wlReorderVariant}
              />
            ) : null}
            {order?.orderShipping?.tracking_url &&
            order.status === SENT_TO_CUSTOMER ? (
              <span className="track-delivery-button">
                <SubmitButton
                  id="detail-row-track-delivery"
                  placeholder={translator.translate(
                    language,
                    "my_account",
                    `MyOrders/details/trackDelivery`
                  )}
                  onSubmit={(e: React.MouseEvent) => {
                    if (e.stopPropagation) e.stopPropagation();
                    MainTracker.track("click", "Track delivery");
                    window.open(order.orderShipping.tracking_url!);
                  }}
                />
                {!isMobile && deliveryEvent ? (
                  <div className="account-order-card-delivery-status-container">
                    <div className="account-order-card-delivery-status-description">
                      {deliveryEvent.description}
                    </div>
                  </div>
                ) : null}
              </span>
            ) : null}
            {(order.paymentMethod.shouldShowPaymentButton ?? order.paymentMethod.isShowPaymentButton) ? (
              <SubmitButton
                id="detail-row-pay-now"
                placeholder={translator.translate(
                  language,
                  "my_account",
                  `MyOrders/details/payOnline`
                )}
                onSubmit={(e: React.MouseEvent) => {
                  if (e.stopPropagation) e.stopPropagation();
                  MainTracker.track("click", "Pay Now");
                  navigate(
                    `/payment/${country}/${language}/${
                      order.hashID?.split("-")[0]
                    }${order.paymentMethod?.isRepayment ? "?repay=true" : ""}`
                  );
                }}
              />
            ) : null}
            {showConsultationBooking ? (
              <div className="consultation-booking-container">
                <div className="consultation-info">
                  <img src={CalendarIcon} alt="calendar" />
                  <div className="consultation-details">
                    <div className="consultation-label">
                      {translator.translate(
                        language,
                        "my_account",
                        "MyOrders/details/consultationScheduled"
                      )}
                    </div>
                    <div className="consultation-datetime">
                      {(() => {
                        const timekitBooking = order.orderTimekitBooking!;
                        // Check if it's a preferred time booking (no doctor_id means preferred time)
                        const isPreferredTimeBooking = !(timekitBooking as any).doctor_id;
                        
                        const localeOptions = getLocaleFormatOptions(`${language.toLowerCase()}-${country.toUpperCase()}`);
                        
                        const dateStr = formatTimestamp(timekitBooking.start, { 
                          showDate: true, 
                          showTime: false,
                          ...localeOptions
                        });
                        
                        if (isPreferredTimeBooking && timekitBooking.end) {
                          // Format time range for preferred time bookings
                          const formatTime = (timestamp: string) => {
                            return formatTimestamp(timestamp, {
                              showDate: false,
                              showTime: true,
                              ...localeOptions
                            });
                          };
                          const timeRange = `${formatTime(timekitBooking.start)}-${formatTime(timekitBooking.end)}`;
                          return `${dateStr} ${timeRange}`;
                        }
                        // Show only start time for doctor-selected bookings
                        return formatTimestamp(timekitBooking.start, { 
                          showDate: true, 
                          showTime: true,
                          ...localeOptions
                        });
                      })()}
                    </div>
                  </div>
                </div>
                <SubmitButton
                  id="detail-row-book-now"
                  placeholder={translator.translate(
                    language,
                    "my_account",
                    `MyOrders/details/joinVideoCall`
                  )}
                  fontsize="14px"
                  isEnabledState={order.orderTimekitBooking!.join_meeting_link !== null}
                  onSubmit={(e: React.MouseEvent) => {
                    if (e.stopPropagation) e.stopPropagation();
                    if (order.orderTimekitBooking?.join_meeting_link) {
                      MainTracker.track('custom', 'Click to join videoconsultation', {...order}) 
                      window.open(order.orderTimekitBooking.join_meeting_link, "_blank");
                    }
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAccountOrderCard;
